import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { createBudgetAccount, deleteBudgetAccount, updateBudgetAccount } from '../../../../services/budgetAPI';
import { buildFormStyles, Pill, TreasuryDrawer } from '../treasury/treasuryUi';

export const NATURE_LABELS = {
  ingreso: 'Ingreso',
  costo: 'Costo',
  gasto: 'Gasto',
};

export const flattenBudgetAccounts = (nodes = [], depth = 0) => {
  const rows = [];
  (nodes || []).forEach((node) => {
    rows.push({
      ...node,
      depth,
      optionLabel: `${'— '.repeat(depth)}${node.account_code} · ${node.name}`,
    });
    rows.push(...flattenBudgetAccounts(node.children, depth + 1));
  });
  return rows;
};

const descendantIds = (node) => {
  const ids = new Set();
  const walk = (current) => {
    (current?.children || []).forEach((child) => {
      ids.add(Number(child.id));
      walk(child);
    });
  };
  walk(node);
  return ids;
};

const emptyForm = (parentId = '') => ({
  name: '',
  account_code: '',
  nature: 'gasto',
  parent_id: parentId ? String(parentId) : '',
  chart_account_id: '',
  notes: '',
});

const accountLeaf = (row) =>
  [
    row.detail_level_6,
    row.detail_level_5,
    row.detail_level_4,
    row.detail_level_3,
    row.detail_level_2,
    row.detail_level_1,
    row.subaccount,
    row.account,
    row.category,
  ]
    .map((value) => String(value || '').trim())
    .find(Boolean) || row.account_code || 'Cuenta';

const BudgetAccountsPanel = ({
  ui,
  isDarkMode = false,
  tree = [],
  chartAccounts = [],
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const [search, setSearch] = useState('');
  const [natureFilter, setNatureFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const flat = useMemo(() => flattenBudgetAccounts(tree), [tree]);
  const byId = useMemo(() => Object.fromEntries(flat.map((row) => [Number(row.id), row])), [flat]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (node) => {
      const natureOk = natureFilter === 'all' || node.nature === natureFilter;
      const searchOk = !q || `${node.account_code} ${node.name} ${node.parent_name}`.toLowerCase().includes(q);
      return natureOk && searchOk;
    };
    const anyMatch = (node) => matches(node) || (node.children || []).some(anyMatch);
    const rows = [];
    const walk = (nodes, depth) => {
      (nodes || []).forEach((node) => {
        if (!anyMatch(node)) return;
        const canExpand = (node.children || []).length > 0;
        const open = q || expanded.size === 0 || expanded.has(Number(node.id));
        rows.push({ ...node, depth, canExpand, expanded: canExpand && open });
        if (canExpand && open) walk(node.children, depth + 1);
      });
    };
    walk(tree, 0);
    return rows;
  }, [tree, search, natureFilter, expanded]);

  const parentOptions = useMemo(() => {
    const blocked = new Set();
    if (editing?.id) {
      blocked.add(Number(editing.id));
      descendantIds(byId[Number(editing.id)] || editing).forEach((id) => blocked.add(id));
    }
    return flat.filter((row) => !blocked.has(Number(row.id)));
  }, [flat, editing, byId]);

  const selectedParent = byId[Number(form.parent_id)] || null;
  const natureLocked = Boolean(selectedParent);

  const chartOptions = useMemo(
    () =>
      (chartAccounts || [])
        .map((row) => ({
          id: row.id,
          label: `${row.account_code} · ${accountLeaf(row)}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [chartAccounts]
  );

  const openCreate = (parentId = '') => {
    const parent = byId[Number(parentId)];
    setEditing(null);
    setForm({
      ...emptyForm(parentId),
      nature: parent?.nature || 'gasto',
    });
    setError('');
    setShowForm(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setForm({
      name: account.name || '',
      account_code: account.account_code || '',
      nature: account.nature || 'gasto',
      parent_id: account.parent_id ? String(account.parent_id) : '',
      chart_account_id: account.chart_account_id ? String(account.chart_account_id) : '',
      notes: account.notes || '',
    });
    setError('');
    setShowForm(true);
  };

  const payloadFromForm = () => ({
    name: form.name,
    account_code: form.account_code,
    nature: natureLocked ? selectedParent.nature : form.nature,
    parent_id: form.parent_id ? Number(form.parent_id) : null,
    chart_account_id: form.chart_account_id ? Number(form.chart_account_id) : null,
    notes: form.notes,
  });

  const handleSave = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = payloadFromForm();
      if (editing?.id) await updateBudgetAccount(editing.id, payload);
      else await createBudgetAccount(payload);
      setShowForm(false);
      await onChanged?.();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la cuenta.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`¿Eliminar ${account.name}?`)) return;
    setBusy(true);
    setError('');
    try {
      await deleteBudgetAccount(account.id);
      await onChanged?.();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la cuenta.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev.size ? prev : flat.map((row) => Number(row.id)));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>Cuentas de presupuesto</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
          Define la jerarquía madre/hija. Las hijas suman en la madre y el resumen se arma con esta estructura.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar cuenta…"
            style={{ ...styles.input, width: 240 }}
          />
          <select value={natureFilter} onChange={(event) => setNatureFilter(event.target.value)} style={{ ...styles.input, width: 160 }}>
            <option value="all">Todas</option>
            <option value="ingreso">Ingresos</option>
            <option value="costo">Costos</option>
            <option value="gasto">Gastos</option>
          </select>
        </div>
        {canEdit ? (
          <button type="button" style={styles.primaryBtn} onClick={() => openCreate()}>
            <Plus size={14} /> Nueva cuenta
          </button>
        ) : null}
      </div>
      {error && !showForm ? <p style={{ margin: '0 0 10px', color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr>
              {['Cuenta', 'Naturaleza', 'Madre', 'Mayor', ''].map((label) => (
                <th
                  key={label || 'actions'}
                  style={{
                    textAlign: 'left',
                    padding: 8,
                    borderBottom: `1px solid ${ui.border}`,
                    fontSize: 11,
                    color: ui.muted,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((account) => {
              const canExpand = account.canExpand;
              return (
                <tr key={account.id}>
                  <td style={{ padding: 8 }}>
                    <button
                      type="button"
                      onClick={() => canExpand && toggle(Number(account.id))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        border: 'none',
                        background: 'transparent',
                        color: ui.text,
                        cursor: canExpand ? 'pointer' : 'default',
                        paddingLeft: (account.depth || 0) * 16,
                        textAlign: 'left',
                      }}
                    >
                      {canExpand ? account.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : <span style={{ width: 14 }} />}
                      <span>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{account.name}</div>
                        <div style={{ fontSize: 11, color: ui.muted }}>{account.account_code}</div>
                      </span>
                    </button>
                  </td>
                  <td style={{ padding: 8 }}>
                    <Pill
                      label={NATURE_LABELS[account.nature] || account.nature}
                      tone={account.nature === 'ingreso' ? 'success' : account.nature === 'costo' ? 'warning' : 'info'}
                      isDarkMode={isDarkMode}
                    />
                  </td>
                  <td style={{ padding: 8, fontSize: 12, color: ui.muted }}>{account.parent_name || '—'}</td>
                  <td style={{ padding: 8, fontSize: 12, color: ui.muted }}>
                    {account.chart_account_label
                      ? `${account.chart_account_code || ''} ${account.chart_account_label}`.trim()
                      : '—'}
                  </td>
                  <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canEdit ? (
                      <>
                        <button type="button" style={styles.ghostBtn} onClick={() => openCreate(account.id)} title="Agregar cuenta hija">
                          <Plus size={13} />
                        </button>
                        <button type="button" style={styles.ghostBtn} onClick={() => openEdit(account)}>
                          <Pencil size={13} />
                        </button>
                        <button type="button" style={styles.ghostBtn} onClick={() => handleDelete(account)} disabled={busy}>
                          <Trash2 size={13} />
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {!visible.length ? (
              <tr>
                <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Crea cuentas madre (Ingresos, Costos, Gastos) y agrégales hijas para armar el estado de resultado.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editing ? 'Editar cuenta' : form.parent_id ? 'Nueva cuenta hija' : 'Nueva cuenta'}
          subtitle="La cuenta hija hereda la naturaleza de la madre y suma en ella."
          error={error}
          saving={busy}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Nombre
            <input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} style={styles.input} />
          </label>
          <label style={styles.label}>
            Código (opcional)
            <input
              value={form.account_code}
              onChange={(event) => setForm((prev) => ({ ...prev, account_code: event.target.value }))}
              placeholder="Se genera automático"
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Cuenta madre
            <select
              value={form.parent_id}
              onChange={(event) => {
                const parent = byId[Number(event.target.value)];
                setForm((prev) => ({
                  ...prev,
                  parent_id: event.target.value,
                  nature: parent?.nature || prev.nature,
                }));
              }}
              style={styles.input}
            >
              <option value="">Sin madre (cuenta raíz)</option>
              {parentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.optionLabel}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Naturaleza
            <select
              value={natureLocked ? selectedParent.nature : form.nature}
              onChange={(event) => setForm((prev) => ({ ...prev, nature: event.target.value }))}
              style={styles.input}
              disabled={natureLocked}
            >
              <option value="ingreso">Ingreso</option>
              <option value="costo">Costo</option>
              <option value="gasto">Gasto</option>
            </select>
          </label>
          <label style={styles.label}>
            Cuenta del mayor (opcional)
            <select
              value={form.chart_account_id}
              onChange={(event) => setForm((prev) => ({ ...prev, chart_account_id: event.target.value }))}
              style={styles.input}
            >
              <option value="">Sin vínculo al mayor</option>
              {chartOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Notas
            <input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} style={styles.input} />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default BudgetAccountsPanel;
